import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSupabase } from "../SupabaseContext";
import SituationsManagement from "./SituationsManagement";
import AccessManagement from "./AccessManagement";
import AdminRegistrationForm from "./AdminRegistrationForm";
import EvaluationMatrix from "./EvaluationMatrix";
import CustomCategoriesManagement from "./CustomCategoriesManagement";
import BulkEmployeeImport from "src/components/BulkEmployeeImport";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  LayoutDashboard,
  Users,
  FileText,
  Lock,
  Search,
  Plus,
  MoreVertical,
  Pencil,
  Badge,
  CheckCircle,
  X,
  Trash2,
  Tags,
} from "lucide-react";
import { Alert, AlertDescription } from "./ui/alert";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const supabase = useSupabase();
  const [activeTab, setActiveTab] = useState("employees");
  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isEmployeeFormOpen, setIsEmployeeFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [userFacilities, setUserFacilities] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [user] = useState(JSON.parse(localStorage.getItem("user")));

  useEffect(() => {
    fetchEmployees();
    fetchFacilitiesAndCategories();
  }, []);

  useEffect(() => {
    // Assuming you already have fetchEmployees()
    fetchEmployees();
    fetchFacilitiesAndCategories();
  }, []);

  const handleOpenDialog = () => {
    setIsDialogOpen(true);
    setSelectedEmployee(null);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedEmployee(null);
  };

  // Add this new function
  const fetchFacilitiesAndCategories = async () => {
    try {
      // First verify we have valid user data
      if (!user?.id || !user?.company_id) {
        console.error("Missing user data");
        return;
      }

      // Get user's facilities based on role
      let facilitiesQuery = supabase
        .from("facilities")
        .select("*")
        .eq("is_active", true);

      if (user.is_manager) {
        // If user is a manager, get their managed facilities
        const { data: managedFacilities, error: managerError } = await supabase
          .from("facility_managers")
          .select("facility_id")
          .eq("user_id", user.id)
          .eq("is_active", true);

        if (managerError) throw managerError;

        if (managedFacilities && managedFacilities.length > 0) {
          // Filter out any null facility_ids just in case
          const validFacilityIds = managedFacilities
            .map((f) => f.facility_id)
            .filter((id) => id != null);

          if (validFacilityIds.length > 0) {
            facilitiesQuery = facilitiesQuery.in("id", validFacilityIds);
          }
        }
      }

      const [facilitiesResult, categoriesResult] = await Promise.all([
        facilitiesQuery,
        supabase
          .from("custom_categories")
          .select(
            `
            id,
            name,
            custom_category_options (
              id,
              value
            )
          `
          )
          .eq("company_id", user.company_id)
          .eq("creator_id", user.id) // This filters
          .eq("is_active", true),
      ]);

      if (facilitiesResult.error) throw facilitiesResult.error;
      if (categoriesResult.error) throw categoriesResult.error;

      setFacilities(facilitiesResult.data || []);
      setCategories(categoriesResult.data || []);
    } catch (error) {
      console.error("Error fetching facilities and categories:", error);
      setError("Failed to load facilities and categories data");
    }
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) {
      navigate("/login");
      return;
    }
    if (user.is_manager) {
      fetchManagerFacilities(user.id);
    } else {
      fetchEmployees();
    }
  }, []);

  const fetchManagerFacilities = async (managerId) => {
    setIsLoading(true);
    try {
      // Get the facilities that this manager manages
      const { data: facilityData, error: facilityError } = await supabase
        .from("facility_managers")
        .select(
          `
          facility_id,
          facilities (
            id,
            name
          )
        `
        )
        .eq("user_id", managerId)
        .eq("is_active", true);

      if (facilityError) {
        console.error("Error fetching facility managers:", facilityError);
        throw facilityError;
      }

      const facilityIds = facilityData.map((f) => f.facility_id);
      setUserFacilities(facilityData);

      if (facilityIds.length === 0) {
        console.log("Manager has no assigned facilities");
        setEmployees([]);
        setIsLoading(false);
        return;
      }

      // Get all users linked to these facilities using the junction table
      const { data: facilityUsers, error: facilityUsersError } = await supabase
        .from("user_facilities")
        .select("user_id")
        .in("facility_id", facilityIds)
        .eq("is_active", true);

      if (facilityUsersError) {
        console.error("Error fetching facility users:", facilityUsersError);
        throw facilityUsersError;
      }

      if (!facilityUsers || facilityUsers.length === 0) {
        console.log("No users found in these facilities");
        setEmployees([]);
        setIsLoading(false);
        return;
      }

      // Get unique user IDs (a user might be in multiple facilities)
      const uniqueUserIds = [...new Set(facilityUsers.map((fu) => fu.user_id))];

      // Fetch the full user details
      const { data: userData, error: userError } = await supabase
        .from("users")
        .select(
          `
          *,
          companies(name),
          solutions(id, situation_id)
        `
        )
        .in("id", uniqueUserIds)
        .eq("is_active", true);

      if (userError) {
        console.error("Error fetching users:", userError);
        throw userError;
      }

      // Get the facilities for each user
      const { data: allUserFacilities, error: userFacilitiesError } =
        await supabase
          .from("user_facilities")
          .select(
            `
          user_id,
          facility_id,
          facilities (
            id,
            name
          )
        `
          )
          .in("user_id", uniqueUserIds)
          .in("facility_id", facilityIds)
          .eq("is_active", true);

      if (userFacilitiesError) {
        console.error("Error fetching user facilities:", userFacilitiesError);
        throw userFacilitiesError;
      }

      // Group facilities by user_id
      const facilitiesByUser = {};
      allUserFacilities.forEach((uf) => {
        if (!facilitiesByUser[uf.user_id]) {
          facilitiesByUser[uf.user_id] = [];
        }
        if (uf.facilities) {
          facilitiesByUser[uf.user_id].push(uf.facilities);
        }
      });

      // Add facilities to each user
      const usersWithFacilities = userData.map((user) => ({
        ...user,
        facilities: facilitiesByUser[user.id] || [],
      }));

      setEmployees(usersWithFacilities);
    } catch (error) {
      console.error("Error fetching facilities:", error);
      setError("Failed to load managed facilities data");
    } finally {
      setIsLoading(false);
    }
  };

  // Inside AdminDashboard.js
  const fetchEmployees = async () => {
    setIsLoading(true);
    try {
      let query;

      if (user.is_manager) {
        // For managers: Get users from facilities they manage
        const { data: managedFacilities } = await supabase
          .from("facility_managers")
          .select("facility_id")
          .eq("user_id", user.id)
          .eq("is_active", true);

        if (!managedFacilities?.length) {
          setEmployees([]);
          return;
        }

        const facilityIds = managedFacilities.map((f) => f.facility_id);

        // Get all users linked to these facilities
        const { data: facilityUsers } = await supabase
          .from("user_facilities")
          .select("user_id")
          .in("facility_id", facilityIds)
          .eq("is_active", true);

        if (!facilityUsers?.length) {
          setEmployees([]);
          return;
        }

        const userIds = [...new Set(facilityUsers.map((fu) => fu.user_id))];

        // Get user details with their facilities
        query = supabase
          .from("users")
          .select(
            `
          *,
          companies(name),
          solutions(id, situation_id)
        `
          )
          .in("id", userIds)
          .eq("is_active", true);
      } else {
        // For admins: Get all users
        query = supabase
          .from("users")
          .select(
            `
          *,
          companies(name),
          solutions(id, situation_id)
        `
          )
          .eq("is_active", true);
      }

      const { data: userData, error } = await query;

      if (error) throw error;

      // Get facilities for each user in a separate query
      const { data: allUserFacilities } = await supabase
        .from("user_facilities")
        .select(
          `
        user_id,
        facility_id,
        facilities(id, name)
      `
        )
        .in(
          "user_id",
          userData.map((u) => u.id)
        )
        .eq("is_active", true);

      // Organize facilities by user_id
      const facilitiesByUser = {};
      allUserFacilities?.forEach((uf) => {
        if (!facilitiesByUser[uf.user_id]) {
          facilitiesByUser[uf.user_id] = [];
        }
        facilitiesByUser[uf.user_id].push(uf.facilities);
      });

      // Add facilities to each user
      const usersWithFacilities = userData.map((user) => ({
        ...user,
        facilities: facilitiesByUser[user.id] || [],
      }));

      setEmployees(usersWithFacilities);
    } catch (error) {
      console.error("Error fetching employees:", error);
      setError("Failed to load employees data");
    } finally {
      setIsLoading(false);
    }
  };

  const filteredEmployees = employees
    .filter((employee) =>
      Object.values(employee).some((value) =>
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    )
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)); // Sort by newest first

  const handleEditEmployee = (employee) => {
    console.log("Employee data being passed to edit:", employee);
    setSelectedEmployee({
      id: employee.id,
      first_name: employee.first_name,
      last_name: employee.last_name,
      email: employee.email,
      position: employee.position,
      id_code: employee.id_code,
      facility_id: employee.facility_id,
      company_id: employee.company_id,
    });
    console.log("Selected employee state after set:", selectedEmployee);
    setIsEmployeeFormOpen(true);
  };

  const handleDeleteEmployee = (employee) => {
    if (employee && employee.id) {
      setSelectedEmployee(employee);
      setIsDeleteDialogOpen(true);
    } else {
      setError("Invalid employee selected for deletion.");
    }
  };

  const confirmDeleteEmployee = async () => {
    if (!selectedEmployee || !selectedEmployee.id) {
      setError("No employee selected for deletion.");
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("users")
        .update({ is_active: false })
        .eq("id", selectedEmployee.id);
      if (error) throw error;

      // Remove the deactivated employee from the local state
      setEmployees(employees.filter((emp) => emp.id !== selectedEmployee.id));

      setIsDeleteDialogOpen(false);
      setSelectedEmployee(null); // Clear the selected employee after deletion
    } catch (error) {
      setError("Failed to deactivate employee. Please try again.");
      console.error("Error deactivating employee:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const tabContent = {
    employees: {
      icon: Users,
      title: "Zamestnanci",
      description: "Správa a prehľad zamestnancov v systéme",
    },
    situations: {
      icon: FileText,
      title: "Situácie",
      description: "Správa situácií pre hodnotenie",
    },
    access: {
      icon: Lock,
      title: "Prístupy",
      description: "Nastavenia prístupov k situáciám",
    },
    solutions: {
      icon: LayoutDashboard,
      title: "Riešenia",
      description: "Prehľad a správa riešení situácií",
    },
    categories: {
      icon: Tags,
      title: "Kategórie",
      description: "Správa kategórií",
    },
  };

  const handleFormSuccess = () => {
    console.log("test");
    fetchEmployees();
    setSelectedEmployee(null);
    setIsEmployeeFormOpen(false);
    console.log("Employee form submitted successfully");
  };

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <Card className="mb-8 shadow-lg">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <LayoutDashboard className="w-8 h-8 text-red-600" />
              <div>
                <CardTitle className="text-2xl font-bold">
                  Admin Dashboard
                </CardTitle>
                <CardDescription>
                  Správa systému Soluteria a jeho používateľov
                </CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="h-20 grid grid-cols-5 gap-4">
          {Object.entries(tabContent).map(([key, { icon: Icon, title }]) => (
            <TabsTrigger
              key={key}
              value={key}
              className="flex flex-col items-center justify-center py-3 px-4 h-full transition-all duration-200 ease-in-out hover:bg-gray-100"
            >
              <div className="flex flex-col items-center space-y-2">
                <Icon className="w-5 h-5" />
                <span className="text-xs font-medium text-center">{title}</span>
              </div>
            </TabsTrigger>
          ))}
        </TabsList>

        {Object.entries(tabContent).map(([key, { title, description }]) => (
          <TabsContent key={key} value={key}>
            <Card className="shadow-md">
              <CardHeader>
                <CardTitle>{title}</CardTitle>
                <CardDescription>{description}</CardDescription>
              </CardHeader>
              <CardContent>
                {key === "employees" && (
                  <>
                    <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
                      <div className="relative w-full sm:w-auto">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <Input
                          placeholder="Vyhľadať zamestnanca..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10 pr-10 w-full sm:w-64"
                        />
                        {searchTerm && (
                          <button
                            onClick={() => setSearchTerm("")}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                            aria-label="Clear search"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      <Button onClick={handleOpenDialog}>
                        <Plus className="w-4 h-4 mr-2" />
                        Pridať zamestnanca
                      </Button>
                    </div>

                    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                      <DialogContent className="max-w-4xl">
                        <DialogHeader>
                          <DialogTitle>Pridať nového zamestnanca</DialogTitle>
                        </DialogHeader>

                        <BulkEmployeeImport
                          onSubmit={() => {
                            fetchEmployees();
                            handleCloseDialog();
                          }}
                          facilities={facilities}
                          categories={categories}
                          supabase={supabase}
                        />
                      </DialogContent>
                    </Dialog>
                    {error && (
                      <Alert variant="destructive" className="mb-4">
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    )}
                    {isLoading ? (
                      <div className="flex justify-center items-center h-32">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Firma</TableHead>
                              <TableHead>Pracovník</TableHead>
                              <TableHead>Pozícia</TableHead>
                              <TableHead>e-mail</TableHead>
                              <TableHead>Heslo</TableHead>
                              <TableHead>Akcie</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filteredEmployees.map(
                              (employee) =>
                                employee.is_active && (
                                  <TableRow key={employee.id}>
                                    <TableCell>
                                      {employee.companies?.name}
                                    </TableCell>
                                    <TableCell>{`${employee.first_name} ${employee.last_name}`}</TableCell>
                                    <TableCell>{employee.position}</TableCell>
                                    <TableCell>{employee.email}</TableCell>
                                    <TableCell>{employee.id_code}</TableCell>

                                    <TableCell>
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            className="h-8 w-8 p-0"
                                          >
                                            <span className="sr-only">
                                              Open menu
                                            </span>
                                            <MoreVertical className="h-4 w-4" />
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                          <DropdownMenuItem
                                            onClick={() =>
                                              handleEditEmployee(employee)
                                            }
                                          >
                                            <Pencil className="mr-2 h-4 w-4" />
                                            <span>Upraviť</span>
                                          </DropdownMenuItem>
                                          <DropdownMenuItem
                                            onClick={() =>
                                              handleDeleteEmployee(employee)
                                            }
                                          >
                                            <Trash2 className="mr-2 h-4 w-4" />
                                            <span>Vymazať</span>
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    </TableCell>
                                  </TableRow>
                                )
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </>
                )}
                {key === "situations" && <SituationsManagement />}
                {key === "solutions" && <EvaluationMatrix />}
                {key === "access" && <AccessManagement />}
                {key === "categories" && <CustomCategoriesManagement />}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      <Dialog
        open={isEmployeeFormOpen}
        onOpenChange={(open) => {
          setIsEmployeeFormOpen(open);
          if (!open) setSelectedEmployee(null);
        }}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {selectedEmployee
                ? "Upraviť Zamestnanca"
                : "Pridať Nového Zamestnanca"}
            </DialogTitle>
          </DialogHeader>
          <AdminRegistrationForm
            initialData={selectedEmployee}
            onSuccess={() => {
              fetchEmployees();
              setSelectedEmployee(null);
              setIsEmployeeFormOpen(false);
            }}
            onClose={() => setIsEmployeeFormOpen(false)}
            facilities={facilities} // Make sure this is getting passed correctly and is non-empty
            categories={categories}
          />
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={(open) => {
          setIsDeleteDialogOpen(open);
          if (!open) setSelectedEmployee(null); // Clear selection when dialog is closed
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Ste si istý, že chcete vymazať tohto zamestnanca?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Táto akcia sa nedá vrátiť späť. Zamestnanec bude označený ako
              neaktívny a nebude sa zobrazovať v zoznamoch.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Zrušiť</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteEmployee}>
              Deaktivovať
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;
